import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import GridFilterPanel from '../../commons/filters/GridFilterPanel.jsx';
import { buildPeriodoOptions } from '../../commons/filters/periodos.js';
import * as Utils from '../../commons/utils/Utils.js';
import { TIPOS_DE_MOVIMIENTO, etiquetaDeTipo } from './inventarioMovimientosTipos.js';
import {
	INVENTARIO_MOVIMIENTOS_FILTROS_STORAGE_KEY,
	buildInventarioMovimientosFilterFields,
	getInventarioMovimientosFilterDefaults,
} from './inventarioMovimientosFilterFields.js';

/**
 * VEC-3315 — filtros del historial global de movimientos.
 *
 * Alimenta a <GridFilterPanel /> con las opciones de sus tres desplegables; el estado, la
 * persistencia y la serialización son del panel compartido. Molde: `MovilesGridFiltros.jsx`.
 *
 * ⚠️ No se toca `commons/filters/GridFilterPanel.jsx`: es compartido con Facturas, Tickets y
 * Móviles. Todo lo que esta pantalla necesita —incluido el desplegable de períodos con su rango
 * personalizado— se resuelve declarando campos.
 */

const OPTIONS_INIT = { periodo: [], tipo: [], ubicacionId: [] };

/** Mismo select de depósitos que consume la grilla de stock y el formulario de ingreso. */
const URL_ALMACENES = '/panol2/select/almacenes';

/**
 * El panel lee siempre `option.value` y `option.label`. Se garantiza que el label nunca sea
 * `undefined`: MUI le pide `.toLowerCase()` al filtrar y un label indefinido tira el render de la
 * pantalla entera, no deja el desplegable vacío.
 */
const aOpciones = (data) =>
	(data || [])
		.filter((e) => e && e.value !== undefined && e.value !== null)
		.map((e) => ({ value: e.value, label: e.label === undefined || e.label === null ? String(e.value) : e.label }));

const InventarioMovimientosGridFiltros = (props) => {
	const intl = useIntl();

	const [options, setOptions] = useState(OPTIONS_INIT);
	const [optionsLoading, setOptionsLoading] = useState(true);

	const fields = buildInventarioMovimientosFilterFields();

	// Las opciones de período y de tipo NO salen de un endpoint: la primera es la lista compartida de
	// atajos (`commons/filters/periodos.js`) y la segunda el catálogo de tipos del modelo. Se arman
	// acá y no se piden.
	const opcionesFijas = () => ({
		periodo: buildPeriodoOptions(intl),
		tipo: TIPOS_DE_MOVIMIENTO.map((tipo) => ({ value: tipo, label: etiquetaDeTipo(intl, tipo) })),
	});

	useEffect(() => {
		setOptionsLoading(true);
		Utils.getData(URL_ALMACENES)
			.then((data) => setOptions(Object.assign({}, opcionesFijas(), { ubicacionId: aOpciones(data) })))
			.catch(() => setOptions(Object.assign({}, OPTIONS_INIT, opcionesFijas())))
			.finally(() => setOptionsLoading(false));
	}, []);

	return (
		<GridFilterPanel
			open={props.open}
			fields={fields}
			options={options}
			defaults={getInventarioMovimientosFilterDefaults(intl)}
			storageKey={INVENTARIO_MOVIMIENTOS_FILTROS_STORAGE_KEY}
			loading={props.loading || optionsLoading}
			// Grilla de React/MUI: trae los datos ella misma a partir del query. Sin esto, los filtros
			// persistidos se ven en los controles pero no están aplicados.
			applyOnMount={true}
			resetSignal={props.resetSignal}
			onResetSignalHandled={props.onResetSignalHandled}
			onApply={props.onApply}
		/>
	);
};

export default InventarioMovimientosGridFiltros;
